'use client';

import { useEffect } from 'react';
import immer from 'immer';
import { unionBy } from 'lodash';
import type { WatchQueryFetchPolicy } from '@apollo/client';
import { useApolloClient, useCursorPagination } from '@frameio/graphql-client';
import type { GetWorkspacesBasicQueryResult } from '@frameio/graphql-client/generated';
import {
  useGetWorkspacesBasicQuery,
  WorkspacesBasicWithProjects,
} from '@frameio/graphql-client/generated';
import { DEFAULT_WORKSPACES_INITIAL_PAGE_COUNT } from '@frameio/shared/constants';

type UseGetWorkspacesInfoProps = {
  accountId: string;
  fetchPolicy?: WatchQueryFetchPolicy;
};

export const useGetWorkspacesInfo = ({
  accountId,
  fetchPolicy,
}: UseGetWorkspacesInfoProps): ReturnType<
  typeof useCursorPagination<GetWorkspacesBasicQueryResult>
> => {
  const { cache } = useApolloClient();

  const result = useCursorPagination(
    useGetWorkspacesBasicQuery({
      variables: {
        accountId,
        page: { first: DEFAULT_WORKSPACES_INITIAL_PAGE_COUNT },
      },
      skip: !accountId,
      fetchPolicy,
    })
  );

  useEffect(() => {
    const data = result.data;
    if (!data) return;
    const newNodes = data.account?.workspaces?.nodes ?? [];

    if (!data.account?.id) return;

    const fragmentOptions = {
      fragment: WorkspacesBasicWithProjects,
      fragmentName: 'WorkspacesBasicWithProjects',
      id: cache.identify({ __typename: 'Account', id: data.account.id }),
    };

    const existing = cache.readFragment(fragmentOptions);

    if (existing) {
      cache.updateFragment(fragmentOptions, (value) => {
        if (!value) return value;
        return immer(value, (draft) => {
          draft.workspaces = {
            ...draft.workspaces,
            nodes: unionBy(
              draft.workspaces.nodes,
              newNodes,
              (workspaceNode) => workspaceNode.id
            ),
          };
        });
      });
    } else {
      cache.writeFragment({
        ...fragmentOptions,
        data: {
          __typename: 'Account',
          id: data.account.id,
          workspaces: {
            ...data.account?.workspaces,
            nodes: newNodes,
          },
        },
      });
    }
    // Only fire when a new completed result arrives (mirrors `onCompleted`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.data]);

  return result;
};
